// Lógica pura (sin Appwrite, sin React) para vacaciones de trabajadores privados (Código del Trabajo, Art. 69-78).
// Las fechas son strings "YYYY-MM-DD" y se operan en UTC para evitar bugs de zona horaria.

import { Query, type Models } from "appwrite";
import type { Holiday } from "../holidays/types";
import { VACATION_CONFIG, type VacationConfig } from "./lib/vacationConfig";
import { databases } from "@/lib/appwrite";

const DATABASE_ID = import.meta.env.VITE_APPWRITE_DATABASE_ID!;
const WORKER_PROFILE_COLLECTION_ID = import.meta.env.VITE_WORKER_PROFILE_COLLECTION_ID!;

type WorkerDoc = Models.Document & {
    userId: string;
    firstName?: string;
    lastName?: string;
};


const DAY_MS = 86_400_000;

// ---------- Fechas ----------

export const parseDate = (dateString: string): Date => {
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(Date.UTC(year, month - 1, day));
};

export const toISODate = (date: Date): string => date.toISOString().slice(0, 10);

// Datetime de Appwrite ("1990-05-10T00:00:00.000+00:00") -> "1990-05-10"
export const fromAppwriteDatetime = (dateTime: string): string => dateTime.slice(0, 10);

// "YYYY-MM-DD" -> datetime de Appwrite (medianoche UTC, para que no se corra un día)
export const toAppwriteDatetime = (date: string): string => `${date}T00:00:00.000+00:00`;

// Para date pickers (fecha local) -> "YYYY-MM-DD"
export const localDateToISO = (date: Date): string =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const addYears = (iso: string, years: number): string => {
    const date = parseDate(iso);
    date.setUTCFullYear(date.getUTCFullYear() + years);
    return toISODate(date);
};

const daysBetween = (startDate: string, endDate: string): number =>
    Math.round((parseDate(endDate).getTime() - parseDate(startDate).getTime()) / DAY_MS);

// Convención de nómina: año de 360 días, meses de 30
const days360 = (startDate: string, endDate: string): number => {
    const [startYear, StartMonth, StartDayRaw] = startDate.split("-").map(Number);
    const [endYear, EndMonth, EndDayRaw] = endDate.split("-").map(Number);
    const d1 = Math.min(StartDayRaw, 30);
    const d2 = EndDayRaw === 31 && d1 === 30 ? 30 : EndDayRaw;
    return (endYear - startYear) * 360 + (EndMonth - StartMonth) * 30 + (d2 - d1);
};

export function yearsCompleted(from: string, asOf: string): number {
    const startDate = parseDate(from);
    const asOfDate = parseDate(asOf);
    let years = asOfDate.getUTCFullYear() - startDate.getUTCFullYear();
    const notYet =
        asOfDate.getUTCMonth() < startDate.getUTCMonth() ||
        (asOfDate.getUTCMonth() === startDate.getUTCMonth() && asOfDate.getUTCDate() < startDate.getUTCDate());
    if (notYet) years--;
    return Math.max(0, years);
}

const sameCanton = (firstCanton?: string | null, secondCanton?: string | null): boolean =>
    !!firstCanton && !!secondCanton && firstCanton.trim().toLowerCase() === secondCanton.trim().toLowerCase();

// ------Días a los que tiene derecho ----

/**
 * Días de descanso del período anual número `period` (1 = primer año completo).
 * Menores de 16: 20. De 16 a 17: 18. Adultos: 15 + 1 por cada año después del 5to (tope 15 extra),
 * salvo que los adicionales se paguen en dinero (extraDaysMode "cash"), en cuyo caso solo cuentan los 15.
 */
export function daysForPeriod(
    period: number,
    age: number,
    config: VacationConfig = VACATION_CONFIG
): number {
    if (age < 16) return 20;
    if (age < 18) return 18;
    if (config.extraDaysMode === "cash") return config.baseDaysPerYear;
    const extra = Math.min(Math.max(period - 5, 0), config.maxExtraDays);
    return config.baseDaysPerYear + extra;
}

export type BalanceSummary = {
    yearsOfService: number;
    daysEarned: number; // solo períodos completos
    daysTaken: number;
    availableBalance: number; // lo que puede tomar hoy
    accruedInCurrentPeriod: number; // no gozable aún, sí liquidable
    accumulatedPeriods: number;
    hrAlert: boolean; // true al llegar a 3 períodos acumulados (Art. 75)
};

export function calculateBalance(params: {
    joinDate: string;
    birthday: string;
    asOf: string;
    daysTaken: number;
    config?: VacationConfig;
}): BalanceSummary {
    const { joinDate, birthday, asOf, daysTaken, config = VACATION_CONFIG } = params;
    const years = yearsCompleted(joinDate, asOf);

    let daysEarned = 0;
    for (let number = 1; number <= years; number++) {
        const periodEnd = addYears(joinDate, number);
        daysEarned += daysForPeriod(number, yearsCompleted(birthday, periodEnd), config);
    }

    const availableBalance = Math.max(0, daysEarned - daysTaken);

    // Proporcional del período en curso: días del período / base (360 o 365) por día transcurrido
    const lastAnniversary = addYears(joinDate, years);
    const elapsed = Math.max(
        0,
        config.prorationBaseDays === 360
            ? days360(lastAnniversary, asOf)
            : daysBetween(lastAnniversary, asOf)
    );
    const ageNow = yearsCompleted(birthday, asOf);
    const currentBase = daysForPeriod(years + 1, ageNow, config);
    const accruedInCurrentPeriod =
        Math.round(((currentBase * elapsed) / config.prorationBaseDays) * 100) / 100;

    const referencePeriodDays = daysForPeriod(Math.max(years, 1), ageNow, config);
    const accumulatedPeriods = Math.round((availableBalance / referencePeriodDays) * 100) / 100;

    return {
        yearsOfService: years,
        daysEarned,
        daysTaken,
        availableBalance,
        accruedInCurrentPeriod,
        accumulatedPeriods,
        hrAlert: accumulatedPeriods >= config.maxAccumulatedPeriods,
    };
}

// ---------- Solicitudes (período continuo) ----------

export type RequestResult = {
    ok: boolean;
    error?: string;
    calendarDays: number;
    holidaysInRange: Holiday[];
    workingDays: number;
    daysToDeduct: number;
};

/**
 * Período continuo de descanso. Cuenta días calendario (Art. 69 incluye los no laborables).
 * Los feriados se descuentan según config.holidaysCountInContinuousPeriod.
 * Filtro por ubicación: feriados nacionales (sin cantón) + los del cantón del empleado.
 */
export function calculateRequest(params: {
    start: string;
    end: string;
    canton: string;
    holidays: Holiday[];
    config?: VacationConfig;
}): RequestResult {
    const { start, end, canton, holidays, config = VACATION_CONFIG } = params;

    const empty = { calendarDays: 0, holidaysInRange: [], workingDays: 0, daysToDeduct: 0 };
    if (parseDate(end) < parseDate(start)) {
        return { ok: false, error: "La fecha fin no puede ser anterior a la de inicio.", ...empty };
    }

    const applicable = new Map<string, Holiday>();
    for (const holiday of holidays) {
        if (!holiday.canton || sameCanton(holiday.canton, canton)) applicable.set(holiday.date, holiday);
    }

    let calendarDays = 0;
    let workingDays = 0;
    let daysToDeduct = 0;
    const holidaysInRange: Holiday[] = [];

    for (let currentTime = parseDate(start).getTime(); currentTime <= parseDate(end).getTime(); currentTime += DAY_MS) {
        const currentDate = new Date(currentTime);
        const holiday = applicable.get(toISODate(currentDate));
        const dayOfWeek = currentDate.getUTCDay();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

        calendarDays++;
        if (holiday) holidaysInRange.push(holiday);
        if (!isWeekend && !holiday) workingDays++;

        if (config.holidaysCountInContinuousPeriod || !holiday) daysToDeduct++;
    }

    // Bloquea solicitudes que solo cubren feriados / fines de semana (no hay nada que "gastar")
    if (workingDays === 0) {
        return {
            ok: false,
            error: "El rango solo incluye feriados o fines de semana; no requiere vacaciones.",
            calendarDays,
            holidaysInRange,
            workingDays,
            daysToDeduct: 0,
        };
    }

    return { ok: true, calendarDays, holidaysInRange, workingDays, daysToDeduct };
}


export async function getWorkerNames(userIds: string[]): Promise<Record<string, string>> {
    const ids = Array.from(new Set(userIds.filter(Boolean)));
    if (ids.length === 0) return {};

    const names: Record<string, string> = {};
    const CHUNK = 100;

    for (let i = 0; i < ids.length; i += CHUNK) {
        const chunk = ids.slice(i, i + CHUNK);
        const response = await databases.listDocuments<WorkerDoc>(
            DATABASE_ID,
            WORKER_PROFILE_COLLECTION_ID,
            [
                Query.equal("userId", chunk),
                Query.select(["userId", "firstName", "lastName"]),
                Query.limit(chunk.length),
            ]
        );
        for (const worker of response.documents) {
            names[worker.userId] = [worker.firstName, worker.lastName].filter(Boolean).join(" ");
        }
    }
    return names;
}